import React, { useState, useEffect, useContext } from "react"
import styled from "styled-components"
import { SectionContext } from "../context/sectionContext"
import { PageContext } from "../context/pageContext"
import { insWords, applyComma, wordCapital, maskAlternateCharsInEmail } from "../../shared/functions"
import { ConditionContext } from "../context/conditionContext"
import { ErrorContext } from "../context/errorContext"
import { isIE } from "react-device-detect"
import { JourneyContext } from "../context/journeyContext"
import { getValueFromJourney, getAge } from "../services/helperFunctions"
import { ifscCodeDetails } from "../../services/apiservice"
import * as lookup from "../../../static/lookup.json"
import { componentRenderCheck, multiConditionComponentRenderCheck } from "../services/componentRenderCheck"
import Parser from 'html-react-parser';

const numToWordFormControlKey = [
  "AnnualIncome",
  "annualIncome",
  "spouseAnnualIncome",
  "sumAssured",
  "CPResidentAndOccupationExistingSumAssured",
  "riderSumAssured",
  "proposerannualIncome",
  "FlexiInstallmentPremium",
  "CFInstallmentPremium",
  "GOPAannualIncome",
  "GOPAproposerAnnualIncome",
  "GSPInstallmentPremium",
  "juvenilSibling",
  "juvenilMother",
  "juvenilFather",
  "GOPAcpInstallmentPremium",
  "jointlifeAnnuaIncome",
  "InsurePremiumAmount",
  "GIAPPremiumAmount",
  "spouseInsuranceAmount",
  "femaleAnnualIncome",
  "JeevanNiveshInstallmentPremium",
  "MoneyBackSumAssured",
  "SumAssuredForWish",
  "invest4gAmountInvest",
  "invest4gSumAssured",
  "GFPInstallmentPremium",
  "secureBhavishyaAmountInvest",
  "guaranteedFutureInstallmentPremium",
  "GI4LSumAssured",
  "GI4LPremiumAmount",
  "GAINpremiumAmount",
  "FutureDollarInstallmentPremium",
  "FutureDollarSumAssured"
]
const InputContainer = styled.div`
  display: block;
  flex-direction: column;
  margin: 15px 0;
  position: relative;
  box-sizing: border-box;
  font-size: 15px;
  color: #505050;
  float: left;

  & > .str-input {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  & input {
    border: none;
    outline: none;
    margin: 0;
    padding: 0;
    line-height: 4;
    height: 42px;
    background: ${props =>
    props.channel === "A"  || props.channel === "28" || props.channel === "35" || props.channel === "39"
      ? "#ededed"
      : "#fff"};
    background: var(--lumiq-bg);
    ${isIE => {
    if (isIE) return `line-height: 3;`
  }}
  }

  & > label {
    margin-left: 8px;
    color: #505050;
    font-size: 16px;
    background: ${props =>
    props.channel === "A"  || props.channel === "28" || props.channel === "35" || props.channel === "39"
      ? "#ededed"
      : "#fff"};
    background: var(--lumiq-bg);
    padding: 2px 5px;
    /* z-index: 999; */
    position: relative;
    display: inline-block;
    cursor: default;
    max-width: 100%;
  }
`

// Helper functions for universal validation
const isPhoneNumberField = (formControlKey) => {
  if (!formControlKey) return false
  const key = formControlKey.toLowerCase()
  return key.includes("contact") || key.includes("mobile") || key.includes("phone")
}

const isPincodeField = (formControlKey) => {
  if (!formControlKey) return false
  return formControlKey.toLowerCase().includes("pincode")
}

const getCountryFieldForSection = (sectionId, formControlKey, sectionData) => {
  const lowerKey = formControlKey?.toLowerCase() || ""

  // Explicit mappings for specific fields
  if (formControlKey === "emppincode" && sectionData?.["empcountry"]) {
    return "empcountry"
  }
  if (formControlKey === "proposerPincode" && sectionData?.["ProposerCountry"]) {
    return "ProposerCountry"
  }
  if (lowerKey === "pincode" && sectionData?.["proposerCommunicationCountry"]) {
    return "proposerCommunicationCountry"
  }

  // For appointeePincode, prefer appointeeCountry when present (regardless of addressSameAsLaOthers)
  if (lowerKey.includes("appointee") && lowerKey.includes("pincode") && sectionData?.["appointeeCountry"]) {
      return "appointeeCountry"
  }
  
  // Common country field patterns
  const countryFields = [
    "country", "countryofbirth", "proposerCountry", "currentCountryofResidence",
    "GIFTcountry", "countryGiftCity", "jointlifeCommunicationCountry",
    "jointlifePermanentAddressCountry", "eaCountry"
  ]
  
  // Try to find country field in section data
  for (const field of countryFields) {
    if (sectionData && sectionData[field]) {
      return field
    }
  }
  
  // Section-specific mappings
  const sectionCountryMap = {
    "SectionCommonResidenceAndOccupation": "countryofbirth",
    "proposerCommonResidenceAndOccupation": "proposerCountry",
    "SectionFlexiEdgeFillProposalPersonalDetail": "currentCountryofResidence",
    "SectionFlexiEdgeFillProposalProposerDetails": "proposerCountry",
    "FutureDollarGiftCityPersonelDetailsSection": "GIFTcountry",
    "FlexiEdgeFillProposalAppointeeSection": "appointeeCountry"
  }
  
  return sectionCountryMap[sectionId] || "country"
}

const updatePhoneValidation = (journeySteps, pageId, sectionId, componentId, formControlKey, isIndia) => {
  try {
    const tempPageIndex = journeySteps.pages.findIndex(page => page.pageId === pageId)
    if (tempPageIndex === -1) return false
    
    const tempSectionIndex = journeySteps.pages[tempPageIndex].sections.findIndex(section => section.sectionId === sectionId)
    if (tempSectionIndex === -1) return false
    
    const tempComponentGroupIndex = journeySteps.pages[tempPageIndex].sections[tempSectionIndex].component_groups.findIndex(cg => cg.componentId === componentId)
    if (tempComponentGroupIndex === -1) return false
    
    const tempComponentIndex = journeySteps.pages[tempPageIndex].sections[tempSectionIndex].component_groups[tempComponentGroupIndex].components.findIndex(comp => comp.formControlKey === formControlKey)
    if (tempComponentIndex === -1) return false
    
    const component = journeySteps.pages[tempPageIndex].sections[tempSectionIndex].component_groups[tempComponentGroupIndex].components[tempComponentIndex]
    
    if (isIndia) {
      component.min_len = 10
      component.max_length = 10
      component.max_len = 10
      component.regex = "/^[0-9]{10}$/"
    } else {
      component.min_len = 7
      component.max_length = 15
      component.max_len = 15
      component.regex = "/^[0-9]+$/"
    }
    
    return true
  } catch (error) {
    console.error("Error updating phone validation:", error)
    return false
  }
}

const updatePincodeValidation = (journeySteps, pageId, sectionId, componentId, formControlKey, isIndia) => {
  try {
    const tempPageIndex = journeySteps.pages.findIndex(page => page.pageId === pageId)
    if (tempPageIndex === -1) return false
    
    const tempSectionIndex = journeySteps.pages[tempPageIndex].sections.findIndex(section => section.sectionId === sectionId)
    if (tempSectionIndex === -1) return false
    
    const tempComponentGroupIndex = journeySteps.pages[tempPageIndex].sections[tempSectionIndex].component_groups.findIndex(cg => cg.componentId === componentId)
    if (tempComponentGroupIndex === -1) return false
    
    const tempComponentIndex = journeySteps.pages[tempPageIndex].sections[tempSectionIndex].component_groups[tempComponentGroupIndex].components.findIndex(comp => comp.formControlKey === formControlKey)
    if (tempComponentIndex === -1) return false
    
    const component = journeySteps.pages[tempPageIndex].sections[tempSectionIndex].component_groups[tempComponentGroupIndex].components[tempComponentIndex]
    
    if (isIndia) {
      component.min_len = 6
      component.max_length = 6
      component.max_len = 6
      component.regex = null
      component.__typename = "ComponentCmpTagNumber"
      component.min_len_error = "N0002"
      component.max_len_error = "M0002"
    } else {
      component.min_len = 3
      component.max_length = 9
      component.max_len = 9
      component.regex = "/^[a-zA-Z0-9 ]+$/"
      component.__typename = "ComponentCmpTagTextString"
      component.min_len_error = "N0015"
      component.max_len_error = "M0023"
    }
    
    return true
  } catch (error) {
    console.error("Error updating pincode validation:", error)
    return false
  }
}

export default function ComponentTextString(props) {
  // console.log(props)
  const [sectionState, setSectionState] = useContext(SectionContext)
  const [pageState, setPageState] = useContext(PageContext)
  const [conditionState, setConditionState] = useContext(ConditionContext)
  const [errorState, setErrorState] = useContext(ErrorContext)
  const [journeyState, setJourneyState] = useContext(JourneyContext)
  const [showIns, setShowIns] = useState(true)
  const [showLocalError, setShowLocalError] = useState(false)

  const validate = target => {
    if (props.data.required) {
      console.log("sssssssssssssss", props.data, props.data.formControlKey)
      // required field validation
      if (props.data.not_rq_conds && props.data.not_rq_conds.length > 0) {
        if (props.data.not_rq_conds.length === 1 &&
          !componentRenderCheck(props.data.not_rq_conds[0], conditionState[props.data.not_rq_conds[0].watchKey], journeyState)) {
          setErrorState(prev => {
            return ({
              ...prev,
              [props.data.formControlKey]: props.data.requiredError
                ? (getValueFromJourney(
                  journeyState.lookup.required_error,
                  props.data.requiredError
                )) : `Error Code Not Found in Strapi`,
            })
          })
        }
        else if (props.data.not_rq_conds.length > 1 && !multiConditionComponentRenderCheck(props.data.not_rq_conds, conditionState, journeyState)) {
          setErrorState(prev => {
            return ({
              ...prev,
              [props.data.formControlKey]: props.data.requiredError
                ? (getValueFromJourney(
                  journeyState.lookup.required_error,
                  props.data.requiredError
                )) : `Error Code Not Found in Strapi`,
            })
          })
        }
        else {
          setErrorState(prev => {
            return ({
              ...prev,
              [props.data.formControlKey]: ""
            })
          })
        }
      }
      else if (
        sectionState.sectionData?.[props.data.formControlKey] === "" ||
        !sectionState.sectionData?.[props.data.formControlKey]
      ) {
        console.log(errorState, "errorState")
        setErrorState(prev => {
          return {
            ...prev,
            [props.data.formControlKey]: props.data.requiredError
              ? (getValueFromJourney(
                journeyState.lookup.required_error,
                props.data.requiredError
              )) : `Error Code Not Found in Strapi`,
          }
        })
        return
      } else {
        setErrorState(prev => {
          return {
            ...prev,
            [props.data.formControlKey]: "",
          }
        })
      }
      if (target.value) {
        setErrorState(prev => {
          return {
            ...prev,
            [props.data.formControlKey]: "",
          }
        })
      }
    }
    if (target.value) {
      // Special validation for pincode outside India - minimum length should be 3
      if (isPincodeField(props.data.formControlKey)) {
        const countryFields = [
          "country", "countryofbirth", "proposerCountry", "currentCountryofResidence",
          "GIFTcountry", "countryGiftCity", "jointlifeCommunicationCountry",
          "jointlifePermanentAddressCountry", "eaCountry"
        ]
        let countryValue = null
        // Check sectionState first
        for (const field of countryFields) {
          if (sectionState.sectionData?.[field]) {
            countryValue = sectionState.sectionData[field]
            break
          }
        }
        // Also check conditionState if not found in sectionState
        if (!countryValue) {
          for (const field of countryFields) {
            const conditionKey = Object.keys(conditionState).find(key => key.includes(field))
            if (conditionKey && conditionState[conditionKey]) {
              countryValue = conditionState[conditionKey]
              break
            }
          }
        }
        // If country is not India and pincode length is less than 3
        if (countryValue && countryValue !== "IN" && target.value.length > 0 && target.value.length < 3) {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data.formControlKey]: "Minimum length should be 3"
            }
          })
          setShowLocalError(true)
          return
        }
      }
      
      if (props.data.min_len) {
        // min_len validation
        if (target.value.length < props.data.min_len) {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data
                .formControlKey]: (getValueFromJourney(
                  journeyState.lookup.min_error,
                  props.data.min_len_error
                ))
            }
          })
          setShowLocalError(true)
          return
        } else {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data.formControlKey]: "",
            }
          })
        }
      }
      if (props.data.max_length) {
        //max length validation
        if (target.value.length > props.data.max_length) {
          // Special handling for pincode outside India - show correct max length (9)
          if (isPincodeField(props.data.formControlKey)) {
            const countryFields = [
              "country", "countryofbirth", "proposerCountry", "currentCountryofResidence",
              "GIFTcountry", "countryGiftCity", "jointlifeCommunicationCountry",
              "jointlifePermanentAddressCountry", "eaCountry"
            ]
            let countryValue = null
            // Check sectionState first
            for (const field of countryFields) {
              if (sectionState.sectionData?.[field]) {
                countryValue = sectionState.sectionData[field]
                break
              }
            }
            // Also check conditionState if not found in sectionState
            if (!countryValue) {
              for (const field of countryFields) {
                const conditionKey = Object.keys(conditionState).find(key => key.includes(field))
                if (conditionKey && conditionState[conditionKey]) {
                  countryValue = conditionState[conditionKey]
                  break
                }
              }
            }
            // If country is not India, show max length as 9
            if (countryValue && countryValue !== "IN") {
              setErrorState(prev => {
                return {
                  ...prev,
                  [props.data.formControlKey]: `${(getValueFromJourney(
                    journeyState.lookup.label,
                    props.data.label
                  )) || props.data.label} can maximum be of 9 characters`
                }
              })
              setShowLocalError(true)
              return
            }
          }
          
          if (props.data.max_len_error) {
            setErrorState(prev => {
              return {
                ...prev,
                [props.data.formControlKey]: (getValueFromJourney(
                  journeyState.lookup.max_error,
                  props.data.max_len_error
                ))
              }
            })
          } else {
            setErrorState(prev => {
              return {
                ...prev,
                [props.data.formControlKey]: `${(getValueFromJourney(
                  journeyState.lookup.label,
                  props.data.label
                )) || props.data.label} can maximum be of ${props.data.max_length} characters`,

              }
            })
          }

          setShowLocalError(true)
          return
        } else {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data.formControlKey]: "",
            }
          })
        }
      }
      if (props.data.min_value) {
        console.log(props.data.min_value, " ", target.value);
        let valueToCompare = target.value.replace(/\,/g, '')
        let value = parseInt(valueToCompare)
        console.log(typeof (value))
        if (value < props.data.min_value) {
            setErrorState(prev => {
              return {
                ...prev,
                [props.data
                  .formControlKey]: (getValueFromJourney(
                    journeyState.lookup.min_error,
                    props.data.min_value_error
                  ))

              }
            })
            setShowLocalError(true)
            return
        }
        else {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data
                .formControlKey]: ""

            }
          })
        }
      }
      if (props.data.max_value) {
        let valueToCompare = target.value.replace(/\,/g, '')
        let value = parseInt(valueToCompare)
        console.log(typeof (value))
        if (value > props.data.max_value) {
          if (props.data.label) {
            setErrorState(prev => {
              return {
                ...prev,
                [props.data
                  .formControlKey]: `${(getValueFromJourney(
                    journeyState.lookup.label,
                    props.data.label
                  )) || props.data.label} can't be more than ${props.data.max_value}`,

              }
            })
            setShowLocalError(true)
            return
          } else {
            setErrorState(prev => {
              return {
                ...prev,
                [props.data
                  .formControlKey]: (getValueFromJourney(
                    journeyState.lookup.max_value,
                    props.data.max_value_error
                  ))

              }
            })
            setShowLocalError(true)
            return
          }
        } else {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data
                .formControlKey]: ""

            }
          })
        }
      }
      if (props.data.regex) {
        let regex = props.data.regex.toString()
        let rg = regex.substring(1, regex.lastIndexOf("/"))
        let flags = regex.substring(regex.lastIndexOf("/") + 1)
        let regex_exp = new RegExp(rg, flags)
        if (target.value.indexOf("<") > -1) {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data
                .formControlKey]: `Please enter valid ${(getValueFromJourney(
                  journeyState.lookup.label,
                  props.data.label
                )) || props.data.label}`,
            }
          })
          setShowLocalError(true)
          return
        }
        else if (target.value.indexOf(">") > -1) {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data
                .formControlKey]: `Please enter valid ${(getValueFromJourney(
                  journeyState.lookup.label,
                  props.data.label
                )) || props.data.label}`,
            }
          })
          setShowLocalError(true)
          return
        }
        else if (target.value.indexOf("{") > -1) {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data
                .formControlKey]: `Please enter valid ${(getValueFromJourney(
                  journeyState.lookup.label,
                  props.data.label
                )) || props.data.label}`,
            }
          })
          setShowLocalError(true)
        }
        else if (target.value.indexOf("}") > -1) {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data
                .formControlKey]: `Please enter valid ${(getValueFromJourney(
                  journeyState.lookup.label,
                  props.data.label
                )) || props.data.label}`,
            }
          })
          setShowLocalError(true)
          return
        }
        else if (target.value.indexOf("[") > -1) {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data
                .formControlKey]: `Please enter valid ${(getValueFromJourney(
                  journeyState.lookup.label,
                  props.data.label
                )) || props.data.label}`,
            }
          })
          setShowLocalError(true)
          return
        }
        else if (target.value.indexOf("]") > -1) {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data
                .formControlKey]: `Please enter valid ${(getValueFromJourney(
                  journeyState.lookup.label,
                  props.data.label
                )) || props.data.label}`,
            }
          })
          setShowLocalError(true)
          return
        }
        else if (target.value.indexOf("?") > -1) {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data
                .formControlKey]: `Please enter valid ${(getValueFromJourney(
                  journeyState.lookup.label,
                  props.data.label
                )) || props.data.label}`,
            }
          })
          setShowLocalError(true)
          return
        }
        else if (target.value.indexOf("^") > -1) {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data
                .formControlKey]: `Please enter valid ${(getValueFromJourney(
                  journeyState.lookup.label,
                  props.data.label
                )) || props.data.label}`,
            }
          })
          setShowLocalError(true)
          return
        }

        else if (regex_exp.test(target.value)) {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data.formControlKey]: "",
            }
          })
        } else {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data.formControlKey]: (getValueFromJourney(
                journeyState.lookup.required_error,
                props.data.regexError
              ))
            }
          })
          setShowLocalError(true)
          return
        }
      }
      
      if(target.value){
        let pattern = /[\<\>\[\{\]\}\^\?\(\)]/
        let n = pattern.test(target.value.toString())
        if(n){
          setErrorState(prev => {
            return {
              ...prev,
              [props.data.formControlKey]: `Please enter valid ${(getValueFromJourney(
                journeyState.lookup.label,
                props.data.label
              )) || props.data.label}`
            }
          })
          setShowLocalError(true)
        }
      }

      if (
        (props.sectionId === "SectionFlexiEdgeFillProposalPersonalDetail" &&
          props.data.formControlKey === "taxIdentificationNumber") ||
        (props.sectionId === "SectionFlexiEdgeFillProposalProposerDetails" &&
          props.data.formControlKey === "taxIdentificationNumberProposer")
      ) {
        let conditionStateKey
        if (props.data.formControlKey === "taxIdentificationNumber") {
          conditionStateKey = "PageFlexiEdgeFillProposalForm.SectionFlexiEdgeFillProposalPersonalDetail.taxResidenceCountry"
        } else if (props.data.formControlKey === "taxIdentificationNumberProposer") {
          conditionStateKey = "PageFlexiEdgeFillProposalForm.SectionFlexiEdgeFillProposalProposerDetails.taxResidenceCountryProposer"
        }

        if (conditionState[conditionStateKey]) {
          let regex = lookup.countryRegex?.[0]?.[conditionState[conditionStateKey]]
          if (!regex) {
            return
          }
          let rg = regex.substring(1, regex.lastIndexOf("/"))
          let flags = regex.substring(regex.lastIndexOf("/") + 1)
          let regex_exp = new RegExp(rg, flags)
          if (regex_exp.test(target.value)) {
            setErrorState(prev => {
              return {
                ...prev,
                [props.data.formControlKey]: "",
              }
            })
          } else {
            setErrorState(prev => {
              return {
                ...prev,
                [props.data.formControlKey]: (
                  getValueFromJourney(
                    journeyState.lookup.required_error,
                    props.data.regexError
                  )
                ),
              }
            })
            setShowLocalError(true)
            return
          }
        }
      }
    } else {
      setErrorState(prev => {
        return {
          ...prev,
          [props.data.formControlKey]: "",
        }
      })
    }
    return
  }

  useEffect(() => {
    if(sectionState?.sectionData?.["addressProof"]){
      let aadhar = conditionState["PageFlexiEdgeFillProposalForm.SectionFlexiEdgeFillProposalPersonalDetail.aadharNumber"]
      setSectionState(prev => {
        return ({
          ...prev,
          sectionData: {
            ...prev.sectionData,
            aadharNumber: aadhar
          }
        })
      })
    }
    else if(sectionState?.sectionData?.["currentAddressProof"] && sectionState?.sectionId === "SectionFlexiEdgeFillProposalProposerDetails"){
      let aadhar = conditionState["PageFlexiEdgeFillProposalForm.SectionFlexiEdgeFillProposalProposerDetails.aadharNumber"]
      setSectionState(prev => {
        return ({
          ...prev,
          sectionData: {
            ...prev.sectionData,
            aadharNumber: aadhar
          }
        })
      })
    }
  }, [sectionState?.sectionData?.["addressProof"]])

  useEffect(() => {
    validate({
      value: sectionState.sectionData?.[props.data.formControlKey] ?? "",
    })
  }, [conditionState["PageFlexiEdgeFillProposalForm.SectionFlexiEdgeFillProposalPersonalDetail.taxResidenceCountry"], conditionState["PageFlexiEdgeFillProposalForm.SectionFlexiEdgeFillProposalProposerDetails.taxResidenceCountryProposer"]])

  useEffect(() => {
    if(sectionState.sectionData?.[props.data.formControlKey]){
      dataMask({
        value: sectionState.sectionData?.[props.data.formControlKey] ?? "",
      },"mask")
    }
  },[sectionState?.sectionFirstPass, sectionState?.sectionId])

  useEffect(() => {
    validate({
      value: sectionState.sectionData?.[props.data.formControlKey] ?? "",
    })
  }, [sectionState.sectionData?.[props.data.formControlKey]], props.reqCondWatchKeys?.length && conditionState[props.reqCondWatchKeys[0]],
    props.reqCondWatchKeys?.length == 2 && conditionState[props.reqCondWatchKeys[1]])

  useEffect(() => {
    let currentSectionDataInPageState = pageState.pageData.filter(element => {
      return element.sectionId === props.sectionId
    })
    let formControlKeyPresentInPageState = currentSectionDataInPageState?.[0]?.sectionData?.[props.data.formControlKey]
    if (!(sectionState.sectionData?.[props.data.formControlKey] || formControlKeyPresentInPageState)) {
      if (props?.data?.defaultValue) {
        setSectionState(prev => {
          return {
            ...prev,

            sectionData: {
              ...prev.sectionData,

              [props.data.formControlKey]: props.data.defaultValue,
              [props.data.maskedKey]: props.data.defaultValue,
            },
          }
        })
        setConditionState(prev => {
          return {
            ...prev,
            [`${props.pageId}.${props.sectionId}.${props.data.formControlKey}`]: props
              .data.defaultValue,
          }
        })
        validate(props.data.defaultValue)
        checkForAnnualIncome()
        if (props.saveApplicationAndCallBi)
          props.saveApplicationAndCallBi(props.data.formControlKey, props.data.defaultValue)
      }
    }
    if (props.sectionId === "CPPersonalDetailsSection") {
      if (!sectionState.sectionData[props.data.formControlKey]) {
        if (props.data.formControlKey === "sumAssured") {
          setSectionState(prev => {
            return {
              ...prev,
              sectionData: {
                ...prev.sectionData,
                [props.data.formControlKey]: "10000000",
                [props.data.maskedKey]: "10000000",
              },
            }
          })
          setConditionState(prev => {
            return {
              ...prev,
              [`${props.pageId}.${props.sectionId}.${props.data.formControlKey}`]: "10000000",
            }
          })
        } else if (props.data.formControlKey === "policyTerm") {
          setSectionState(prev => {
            return {
              ...prev,
              sectionData: {
                ...prev.sectionData,
                [props.data.formControlKey]: "20",
              },
            }
          })
          setConditionState(prev => {
            return {
              ...prev,
              [`${props.pageId}.${props.sectionId}.${props.data.formControlKey}`]: "20",
            }
          })
        }
      }
    }
  }, [props.sectionId])

  useEffect(() => {
    if((sectionState.sectionId === "SectionFlexiEdgeBankDetailsOfProposer") && !sectionState.sectionData?.["ifscCode"]){
      let bankName = lookup["bankName"]
      let temp = journeyState.journeySteps
      let tempPageIndexToChange
      let tempSectionIndexToChange
      let tempComponentGroupIndexToChange
      let tempComponentIndexToChange
      tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
      tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "SectionFlexiEdgeBankDetailsOfProposer")
      tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "bankName")
      tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankName")
      temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankName
      temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = true

      tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "bankBranchName")
      tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankBranchName")
      temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankName
      temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = true
      setJourneyState(prev => {
        return ({
          ...prev,
          journeySteps: temp
        })
      })
    } else  if (props.data.formControlKey.toLowerCase().includes("ifsccode") && (sectionState.sectionData?.["ifscCode"] && sectionState.sectionData?.["ifscCode"].length === 11)) {
      let payload = {
        "MIR-BNK-IFSC-CD": sectionState.sectionData?.["ifscCode"]
      }
      ifscCodeDetails(payload)
        .then(res => {
          console.log(res.data)
         if(res?.data?.allUniqueBankName){ 
            let bankNameList = res?.data?.allUniqueBankName
            let bankName = []
            bankName.push(bankNameList)
            let temp = journeyState.journeySteps
            let tempPageIndexToChange
            let tempSectionIndexToChange
            let tempComponentGroupIndexToChange
            let tempComponentIndexToChange
            tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
            tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "SectionFlexiEdgeBankDetailsOfProposer" || element.sectionId === "FlexiEdgeFillProposalAppointeeSection")
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "bankName" || element.componentId === "nomineeAgeCondition")
            tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankName")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankName
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = false
            // setSectionState(prev => {
            //   return ({
            //     ...prev,
            //     sectionData: {
            //       ...prev.sectionData,
            //       bankName: res?.data?.callFlowResponse?.IfscDetails?.Tablerecord?.record?.['MIR-BNK-NM-T'],
            //       bankBranchName: res?.data?.callFlowResponse?.IfscDetails?.Tablerecord?.record?.['MIR-BNK-BR-NM-T']
            //     }
            //   })
            // })
            setJourneyState(prev => {
              return ({
                ...prev,
                journeySteps: temp
              })
            })
            if(Object.keys(bankNameList)?.length === 1){
              let bank = Object.keys(bankNameList)[0]
              setSectionState(prev => {
                return ({
                  ...prev,
                  sectionData: {
                    ...prev.sectionData,
                    bankName: bank
                  }
                })
              })
            let bankAddressList = res?.data?.bankAddress?.[bank]
            let bankAddress = []
            bankAddress.push(bankAddressList)
            let temp = journeyState.journeySteps
            let tempPageIndexToChange
            let tempSectionIndexToChange
            let tempComponentGroupIndexToChange
            let tempComponentIndexToChange
            tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
            tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "SectionFlexiEdgeBankDetailsOfProposer")
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "bankBranchName")
            tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankBranchName")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankAddress
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = false
            setJourneyState(prev => {
              return ({
                ...prev,
                journeySteps: temp
              })
            })
            if(Object.keys(bankAddressList)?.length === 1){
              let bank = Object.keys(bankAddressList)[0]
              setSectionState(prev => {
                return ({
                  ...prev,
                  sectionData: {
                    ...prev.sectionData,
                    bankBranchName: bank
                  }
                })
              })
            }
            }
          }else{
            let bankName = lookup["bankName"]
            let temp = journeyState.journeySteps
            let tempPageIndexToChange
            let tempSectionIndexToChange
            let tempComponentGroupIndexToChange
            let tempComponentIndexToChange
            tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
            tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "SectionFlexiEdgeBankDetailsOfProposer")
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "bankName")
            tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankName")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankName
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = true
      
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "bankBranchName")
            tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankBranchName")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankName
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = true
            setSectionState(prev => {
              return ({
                ...prev,
                sectionData: {
                  ...prev.sectionData,
                  bankName: "",
                  bankBranchName: ""
                }
              })
            })
            setJourneyState(prev => {
              return ({
                ...prev,
                journeySteps: temp
              })
            })
          }
        })
        .catch((err) => {
          console.log("please enter valid ifsc code", err);
        })
    }
  }, [sectionState?.sectionData?.["ifscCode"]])
 

  useEffect(() => {
    if((sectionState.sectionId === "FlexiEdgeFillProposalAppointeeSection") && !sectionState.sectionData?.["ifscCodeAppointee"]){
      let bankName = lookup["bankName"]
      let temp = journeyState.journeySteps
      let tempPageIndexToChange
      let tempSectionIndexToChange
      let tempComponentGroupIndexToChange
      let tempComponentIndexToChange
      tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
      tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "FlexiEdgeFillProposalAppointeeSection")
      tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "nomineeAgeCondition")
      tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankNameAppointee")
      temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankName
      temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = true

      tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "nomineeAgeCondition")
      tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankBranchNameAppointee")
      temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankName
      temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = true
      setJourneyState(prev => {
        return ({
          ...prev,
          journeySteps: temp
        })
      })
    } else  if (props.data.formControlKey.toLowerCase().includes("ifsccodeappointee") && (sectionState.sectionData?.["ifscCodeAppointee"] && sectionState.sectionData?.["ifscCodeAppointee"].length === 11)) {
      let payload = {
        "MIR-BNK-IFSC-CD": sectionState.sectionData?.["ifscCodeAppointee"]
      }
      ifscCodeDetails(payload)
        .then(res => {
          console.log(res.data)
         if(res?.data?.allUniqueBankName){ 
            let bankNameList = res?.data?.allUniqueBankName
            let bankName = []
            bankName.push(bankNameList)
            let temp = journeyState.journeySteps
            let tempPageIndexToChange
            let tempSectionIndexToChange
            let tempComponentGroupIndexToChange
            let tempComponentIndexToChange
            tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
            tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "FlexiEdgeFillProposalAppointeeSection")
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "nomineeAgeCondition")
            tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankNameAppointee")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankName
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = false
            
            setJourneyState(prev => {
              return ({
                ...prev,
                journeySteps: temp
              })
            })
            if(Object.keys(bankNameList)?.length === 1){
              let bank = Object.keys(bankNameList)[0]
              setSectionState(prev => {
                return ({
                  ...prev,
                  sectionData: {
                    ...prev.sectionData,
                    bankNameAppointee: bank
                  }
                })
              })
            let bankAddressList = res?.data?.bankAddress?.[bank]
            let bankAddress = []
            bankAddress.push(bankAddressList)
            let temp = journeyState.journeySteps
            let tempPageIndexToChange
            let tempSectionIndexToChange
            let tempComponentGroupIndexToChange
            let tempComponentIndexToChange
            tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
            tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "FlexiEdgeFillProposalAppointeeSection")
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "nomineeAgeCondition")
            tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankBranchNameAppointee")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankAddress
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = false
            setJourneyState(prev => {
              return ({
                ...prev,
                journeySteps: temp
              })
            })
            if(Object.keys(bankAddressList)?.length === 1){
              let bank = Object.keys(bankAddressList)[0]
              setSectionState(prev => {
                return ({
                  ...prev,
                  sectionData: {
                    ...prev.sectionData,
                    bankBranchNameAppointee: bank
                  }
                })
              })
            }
            }
          }else{
            let bankName = lookup["bankName"]
            let temp = journeyState.journeySteps
            let tempPageIndexToChange
            let tempSectionIndexToChange
            let tempComponentGroupIndexToChange
            let tempComponentIndexToChange
            tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
            tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "SectionFlexiEdgeBankDetailsOfProposer")
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "nomineeAgeCondition")
            tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankNameAppointee")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankName
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = true
      
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "nomineeAgeCondition")
            tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankBranchNameAppointee")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankName
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = true
            setSectionState(prev => {
              return ({
                ...prev,
                sectionData: {
                  ...prev.sectionData,
                  bankNameAppointee: "",
                  bankBranchNameAppointee: ""
                }
              })
            })
            setJourneyState(prev => {
              return ({
                ...prev,
                journeySteps: temp
              })
            })
          }
        })
        .catch((err) => {
          console.log("please enter valid ifsc code", err);
        })
    }
  }, [sectionState?.sectionData?.["ifscCodeAppointee"]])

  useEffect(() => {
    if (props.journeyIdFromStrapi === "ISelect360Journey" && props.sectionId === "CPPersonalDetailsSection" && props?.data?.formControlKey === "AnnualIncome" && sectionState.sectionData?.[props.data.formControlKey]) {
      checkForAnnualIncome()
    }

  }, [sectionState.sectionData && sectionState.sectionData["contactCountryCode"]])

  useEffect(() => {
    if (props.journeyIdFromStrapi === "ISelect360Journey" && props.sectionId === "iSelect360CPSpouseCoverDetails" && props?.data?.formControlKey === "spouseAnnualIncome" && sectionState.sectionData?.[props.data.formControlKey]) {
      checkForAnnualIncome()
    }

  }, [sectionState.sectionData && sectionState.sectionData["residentStatus"]])

  useEffect(() => {
    if(sectionState?.sectionData?.["addressProof"]){
      let aadhar = conditionState["PageFlexiEdgeFillProposalForm.SectionFlexiEdgeFillProposalPersonalDetail.aadharNumber"]
      setSectionState(prev => {
        return ({
          ...prev,
          sectionData: {
            ...prev.sectionData,
            aadharNumber: aadhar
          }
        })
      })
    }
    else if(sectionState?.sectionData?.["currentAddressProof"] && sectionState?.sectionId === "SectionFlexiEdgeFillProposalProposerDetails"){
      let aadhar = conditionState["PageFlexiEdgeFillProposalForm.SectionFlexiEdgeFillProposalProposerDetails.aadharNumber"]
      setSectionState(prev => {
        return ({
          ...prev,
          sectionData: {
            ...prev.sectionData,
            aadharNumber: aadhar
          }
        })
      })
    }
  }, [sectionState?.sectionData?.["addressProof"]])

  useEffect(() => {
    if (props.sectionId === "SectionFlexiEdgeFillProposalPersonalDetail" && props.data.formControlKey === "panNumber") {
      if (conditionState["PageFlexiEdgeFillProposalForm.SectionFlexiEdgeFillProposalPersonalDetail.dateOfBirth"]) {
        let laAge = getAge(conditionState["PageFlexiEdgeFillProposalForm.SectionFlexiEdgeFillProposalPersonalDetail.dateOfBirth"])
        console.log(laAge, "laAge")
        if (laAge < 18) {
          let panCardRequiredError = getValueFromJourney(journeyState.lookup.required_error, props.data.requiredError) || "Please Enter Pan Number"
          if (errorState[props.data.formControlKey]?.toLowerCase() === panCardRequiredError?.toLowerCase()) {
            setErrorState(prev => {
              return {
                ...prev,
                [props.data.formControlKey]: "",
              }
            })
          }
        }
      }
    }
  }, [conditionState["PageFlexiEdgeFillProposalForm.SectionFlexiEdgeFillProposalPersonalDetail.dateOfBirth"], sectionState.sectionData?.[props.data.formControlKey], errorState[props.data.formControlKey]])

  // Universal Phone Number Validation - Updates ALL phone fields across ALL pages
  useEffect(() => {
    let temp = journeyState.journeySteps
    let updated = false
    
    // Iterate through all pages
    temp.pages.forEach((page) => {
      // Iterate through all sections
      page.sections.forEach((section) => {
        // Iterate through all component groups
        section.component_groups.forEach((componentGroup) => {
          // Iterate through all components
          componentGroup.components.forEach((component) => {
            if (isPhoneNumberField(component.formControlKey)) {
              let contactCountryCode = null
              let isNomineePhone = false
              const lowerKey = component.formControlKey?.toLowerCase() || ""
              
              // Check if this is a nominee-related phone field
              if (lowerKey.includes("nominee") || lowerKey.includes("contactnominee")) {
                isNomineePhone = true
                // Check countryCodeNominee first, then nomineeCountryCode
                contactCountryCode = sectionState.sectionData?.["countryCodeNominee"] || 
                                     sectionState.sectionData?.["nomineeCountryCode"] ||
                                     conditionState[`${page.pageId}.${section.sectionId}.countryCodeNominee`] ||
                                     conditionState[`${page.pageId}.${section.sectionId}.nomineeCountryCode`]
              } else if (component.formControlKey === "AlternateMobNumber") {
                contactCountryCode = sectionState.sectionData?.["MobAlternateCountryCode"] ||
                                     conditionState[`${page.pageId}.${section.sectionId}.MobAlternateCountryCode`]
              } else if (component.formControlKey === "contactNoproposerResidence") {
                contactCountryCode = sectionState.sectionData?.["proposerNumberResidence"] ||
                                     conditionState[`${page.pageId}.${section.sectionId}.proposerNumberResidence`]
              } else {
                // For other phone fields, use contactCountryCode
                contactCountryCode = sectionState.sectionData?.["contactCountryCode"] ||
                                     conditionState[`${page.pageId}.${section.sectionId}.contactCountryCode`]
              }
              
              if (!contactCountryCode) return
              
              const isIndia = contactCountryCode === "+91"
              
              // Update validation based on country code
              const tempPageIndex = temp.pages.findIndex(p => p.pageId === page.pageId)
              if (tempPageIndex === -1) return
              
              const tempSectionIndex = temp.pages[tempPageIndex].sections.findIndex(s => s.sectionId === section.sectionId)
              if (tempSectionIndex === -1) return
              
              const tempComponentGroupIndex = temp.pages[tempPageIndex].sections[tempSectionIndex].component_groups.findIndex(cg => cg.componentId === componentGroup.componentId)
              if (tempComponentGroupIndex === -1) return
              
              const tempComponentIndex = temp.pages[tempPageIndex].sections[tempSectionIndex].component_groups[tempComponentGroupIndex].components.findIndex(comp => comp.formControlKey === component.formControlKey)
              if (tempComponentIndex === -1) return
              
              const comp = temp.pages[tempPageIndex].sections[tempSectionIndex].component_groups[tempComponentGroupIndex].components[tempComponentIndex]
              
              if (isIndia && comp.formControlKey !== "contactNoproposerResidence" ) {
                // For India, use AlphaWealth pattern
                if (isNomineePhone) {
                  comp.min_len = 10
                  comp.max_len = 10
                  comp.max_length = 10
                  comp.regex = "/(?!9999999999|8888888888|7777777777|6666666666)(^[6-9][0-9]{9}$)/"
                  comp.regexError = "E168"
                } else {
                  comp.min_len = 10
                  comp.max_len = 10
                  comp.max_length = 10
                  comp.regex = "/^[0-9]{10}$/"
                }
              } else {
                // For outside India, use AlphaWealth pattern
                if (isNomineePhone) {
                  comp.min_len = 7
                  comp.max_len = 15
                  comp.max_length = 15
                  comp.regex = null
                  comp.regexError = null
                } else {
                  comp.min_len = 7
                  comp.max_len = 15
                  comp.max_length = 15
                  comp.regex = "/^[0-9]+$/"
                }
              }
              updated = true
            }
          })
        })
      })
    })
    
    if (updated) {
      setJourneyState(prev => ({
        ...prev,
        journeySteps: temp
      }))
    }
  }, [
    sectionState.sectionData?.["contactCountryCode"],
    sectionState.sectionData?.["countryCodeNominee"],
    sectionState.sectionData?.["nomineeCountryCode"],
    sectionState.sectionData?.["MobAlternateCountryCode"],
    sectionState.sectionData?.["proposerNumberResidence"]
  ])

  // ContactNoResidence Validation based on mobileNumberResidence (AlphaWealth pattern)
  useEffect(() => {
    // Get mobileNumberResidence from sectionState or conditionState
    let mobileNumberResidence = sectionState.sectionData?.["mobileNumberResidence"]
    
    // Also check conditionState
    if (!mobileNumberResidence) {
      const conditionKey = Object.keys(conditionState).find(key => key.includes("mobileNumberResidence"))
      if (conditionKey && conditionState[conditionKey]) {
        mobileNumberResidence = conditionState[conditionKey]
      }
    }
    
    if (!mobileNumberResidence) return
    
    const isIndia = mobileNumberResidence === "+91"
    let temp = journeyState.journeySteps
    let updated = false
    
    // Iterate through all pages to find contactNoResidence field
    temp.pages.forEach((page) => {
      page.sections.forEach((section) => {
        section.component_groups.forEach((componentGroup) => {
          componentGroup.components.forEach((component) => {
            if (component.formControlKey === "contactNoResidence") {
              const tempPageIndex = temp.pages.findIndex(p => p.pageId === page.pageId)
              if (tempPageIndex === -1) return
              
              const tempSectionIndex = temp.pages[tempPageIndex].sections.findIndex(s => s.sectionId === section.sectionId)
              if (tempSectionIndex === -1) return
              
              const tempComponentGroupIndex = temp.pages[tempPageIndex].sections[tempSectionIndex].component_groups.findIndex(cg => cg.componentId === componentGroup.componentId)
              if (tempComponentGroupIndex === -1) return
              
              const tempComponentIndex = temp.pages[tempPageIndex].sections[tempSectionIndex].component_groups[tempComponentGroupIndex].components.findIndex(comp => comp.formControlKey === "contactNoResidence")
              if (tempComponentIndex === -1) return
              
              const comp = temp.pages[tempPageIndex].sections[tempSectionIndex].component_groups[tempComponentGroupIndex].components[tempComponentIndex]
              
              if (isIndia && comp.formControlKey !== "contactNoResidence" && comp.formControlKey !== "contactNoproposerResidence"  ) {
                // For India: min_len = 10, max_len = 10, regex = "/^[6-9][0-9]{9}$/"
                comp.min_len = 10
                comp.max_len = 10
                comp.max_length = 10
                comp.regex = "/^[6-9][0-9]{9}$/"
              } else {
                // For outside India: min_len = 7, max_len = 15, regex = "/^[0-9]+$/"
                comp.min_len = 7
                comp.max_len = 15
                comp.max_length = 15
                comp.regex = "/^[0-9]+$/"
              }
              updated = true
            }
          })
        })
      })
    })
    
    if (updated) {
      setJourneyState(prev => ({
        ...prev,
        journeySteps: temp
      }))
    }
  }, [
    sectionState.sectionData?.["mobileNumberResidence"]
  ])

  // Universal Pincode Validation - Updates ALL pincode fields across ALL pages
  useEffect(() => {
    // Get all possible country fields from sectionState
    const countryFields = [
      "country", "countryofbirth", "proposerCountry", "currentCountryofResidence",
      "GIFTcountry", "countryGiftCity", "jointlifeCommunicationCountry",
      "jointlifePermanentAddressCountry", "eaCountry"
    ]
    
    let countryValue = null
    let countryFieldName = null
    
    // Find the first country field that has a value
    for (const field of countryFields) {
      if (sectionState.sectionData?.[field]) {
        countryValue = sectionState.sectionData[field]
        countryFieldName = field
        break
      }
    }
    
    // Also check conditionState for country fields
    if (!countryValue) {
      for (const field of countryFields) {
        const conditionKey = Object.keys(conditionState).find(key => key.includes(field))
        if (conditionKey && conditionState[conditionKey]) {
          countryValue = conditionState[conditionKey]
          countryFieldName = field
          break
        }
      }
    }
    
    if (!countryValue) return
    
    const isIndia = countryValue === "IN"
    let temp = journeyState.journeySteps
    let updated = false
    
    // Iterate through all pages
    temp.pages.forEach((page) => {
      // Iterate through all sections
      page.sections.forEach((section) => {
        // Iterate through all component groups
        section.component_groups.forEach((componentGroup) => {
          // Iterate through all components
          componentGroup.components.forEach((component) => {
            if (isPincodeField(component.formControlKey)) {
              const lowerKey = component.formControlKey?.toLowerCase() || ""

              // Appointee pincode: prefer appointeeCountry whenever available
              if (lowerKey.includes("appointee") && lowerKey.includes("pincode")) {
                  const appointeeCountry = sectionState.sectionData?.["appointeeCountry"] || conditionState[`${page.pageId}.${section.sectionId}.appointeeCountry`]
                  if (appointeeCountry) {
                    const isIndiaAppointee = appointeeCountry === "IN"
                    if (updatePincodeValidation(temp, page.pageId, section.sectionId, componentGroup.componentId, component.formControlKey, isIndiaAppointee)) {
                      updated = true
                  }
                }
              } else {
                // For other pincode fields, use section-specific country or global country
                const sectionCountryField = getCountryFieldForSection(section.sectionId, component.formControlKey, sectionState.sectionData)
                let sectionCountryValue = sectionState.sectionData?.[sectionCountryField]

                // Explicit overrides for misaligned country/pincode pairs
                if (!sectionCountryValue && component.formControlKey === "emppincode") {
                  sectionCountryValue = conditionState[`${page.pageId}.${section.sectionId}.empcountry`] || conditionState["empcountry"]
                }
                if (!sectionCountryValue && component.formControlKey === "proposerPincode") {
                  sectionCountryValue = conditionState[`${page.pageId}.${section.sectionId}.ProposerCountry`] || conditionState["ProposerCountry"]
                }
                if (!sectionCountryValue && lowerKey === "pincode") {
                  sectionCountryValue = sectionState.sectionData?.["proposerCommunicationCountry"] ||
                                        conditionState[`${page.pageId}.${section.sectionId}.proposerCommunicationCountry`]
                }
                
                // Also check conditionState for section country
                if (!sectionCountryValue) {
                  const conditionKey = Object.keys(conditionState).find(key => key.includes(sectionCountryField))
                  if (conditionKey && conditionState[conditionKey]) {
                    sectionCountryValue = conditionState[conditionKey]
                  }
                }
                
                // If section has its own country field, use it; otherwise use the global one
                const useSectionCountry = sectionCountryValue !== undefined && sectionCountryValue !== null && sectionCountryValue !== ""
                const finalCountryValue = useSectionCountry ? sectionCountryValue : countryValue
                const finalIsIndia = finalCountryValue === "IN"
                
                if (updatePincodeValidation(temp, page.pageId, section.sectionId, componentGroup.componentId, component.formControlKey, finalIsIndia)) {
                  updated = true
                }
              }
            }
          })
        })
      })
    })
    
    if (updated) {
      setJourneyState(prev => ({
        ...prev,
        journeySteps: temp
      }))
    }
  }, [
    sectionState.sectionData?.["country"],
    sectionState.sectionData?.["countryofbirth"],
    sectionState.sectionData?.["proposerCountry"],
    sectionState.sectionData?.["currentCountryofResidence"],
    sectionState.sectionData?.["GIFTcountry"],
    sectionState.sectionData?.["countryGiftCity"],
    sectionState.sectionData?.["jointlifeCommunicationCountry"],
    sectionState.sectionData?.["jointlifePermanentAddressCountry"],
    sectionState.sectionData?.["eaCountry"],
    sectionState.sectionData?.["appointeeCountry"],
    sectionState.sectionData?.["appointeeAddressSameAsLaOthers"],
    sectionState.sectionData?.["proposerCommunicationCountry"],
    sectionState.sectionData?.["empcountry"],
    sectionState.sectionData?.["ProposerCountry"]
  ])

  const handleChange = ({ target }) => {
    if (
      numToWordFormControlKey.includes(props.data.formControlKey) ||
      props.data.formControlKey.toLowerCase().includes("sumassured")
    ) {
      let regex = new RegExp(/^\d+$/)
      if (regex.test(target.value.replace(/,/g, ""))) {
        let temp = target.value.split(",")
        target.value = temp.join("")
        // target.value = target.value.replaceAll(",", "")
      } else if (target.value == "") {
        target.value = ""
      } else {
        return
      }
    }

    if (props.data.formControlKey === "FutureDollarInstallmentPremium"){
      if (target.value === "0") { // 0 blocked
         target.value = ""
      }
    }

    if (sectionState.sectionId === props.sectionId) {
      setSectionState(prev => {
        return {
          ...prev,
          sectionData: {
            ...prev.sectionData,
            [props.data.formControlKey]: props.data.showInCaps ? target.value && target.value?.toUpperCase() : props.data.showInLowerCase ? target.value && target.value?.toLowerCase() : wordCapital(target.value ?? ""),
            [props.data.maskedKey]: props.data.showInCaps ? target.value && target.value?.toUpperCase() : props.data.showInLowerCase ? target.value && target.value?.toLowerCase() : wordCapital(target.value ?? ""),
          },
        }
      })
    } else {
      setSectionState(prev => {
        return {
          ...prev,
          sectionId: props.sectionId,
          sectionData: {
            ...prev.sectionData,
            [props.data.formControlKey]: props.data.showInCaps ? target.value && target.value?.toUpperCase() : props.data.showInLowerCase ? target.value && target.value?.toLowerCase() : wordCapital(target.value ?? ""),
            [props.data.maskedKey]: props.data.showInCaps ? target.value && target.value?.toUpperCase() : props.data.showInLowerCase ? target.value && target.value?.toLowerCase() : wordCapital(target.value ?? ""),
          },
        }
      })
    }
    setConditionState(prev => {
      return {
        ...prev,
        [`${props.pageId}.${props.sectionId}.${props.data.formControlKey}`]: props.data.showInCaps ? target.value && target.value?.toUpperCase() : props.data.showInLowerCase ? target.value && target.value?.toLowerCase() : target.value,
      }
    })
    validate(target)
    //console.log(sectionState, "section state")
  }

  const dataMask = (target, name) => {
    let maskValue
    if(name === "mask" && target.value){
      if(numToWordFormControlKey.includes(props.data.formControlKey) ||
      props.data.formControlKey.toLowerCase().includes("sumassured"))(
        setShowIns(false)
      )
      if(target.value?.length > 0 && props.data.maskType === "string"){
        maskValue = target.value.slice(0, 5) + "*****" 
        setSectionState(prev => {
        return ({
          ...prev,
          sectionData: {
            ...prev.sectionData,
            [props.data.maskedKey]: maskValue
          }
        })
      })
      } else if( target.value?.length > 0 && props.data.maskType === "email"){
        maskValue = maskAlternateCharsInEmail(target.value)
        setSectionState(prev => {
        return ({
          ...prev,
          sectionData: {
            ...prev.sectionData,
            [props.data.maskedKey]: maskValue
          }
        })
      })
      } else if(target.value?.length > 0 && props.data.maskType === "number"){
        let mask = target.value.slice(0,5)
        maskValue = `${mask}XXXXX`
        setSectionState(prev => {
          return ({
            ...prev,
            sectionData: {
              ...prev.sectionData,
              [props.data.maskedKey]: maskValue
            }
          })
        })
      }
    } else {
      setShowIns(true)
          setSectionState(prev => {
            return ({
              ...prev,
              sectionData: {
                ...prev.sectionData,
                [props.data.maskedKey]: sectionState?.sectionData[props.data.formControlKey],
              }
            })
          })
        }

}

  // GI4L
  const setSumAssuredValueForGI4L = () => {
    let sumAssuredValue = 0;
      if(sectionState.sectionData?.["GI4LPaymentFrequency"] === "SG") {
        if(sectionState.sectionData?.["GI4LLifeOption"] === "1") {
          sumAssuredValue = sectionState.sectionData?.["GI4LPremiumAmount"] * 1.25
        } else if(sectionState.sectionData?.["GI4LLifeOption"] === "2") {
          sumAssuredValue = sectionState.sectionData?.["GI4LPremiumAmount"] * 10
        }
      } else {
        sumAssuredValue = sectionState.sectionData?.["GI4LPremiumAmount"] * (12 / sectionState.sectionData?.["GI4LPaymentFrequency"]) * 11
      }
      let temp = journeyState.journeySteps;
      const changeJourneySteps = (pageId, sectionId, componentGroup, componentFormControlKey, key, value) => {
        let tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === pageId)
        let tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === sectionId)
        let tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === componentGroup)
        let tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === componentFormControlKey)
        temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange][key] = value
      }

      if(sumAssuredValue) {
        let tempSection = sectionState.sectionData;
        tempSection["GI4LSumAssured"] = applyComma(Math.round(sumAssuredValue));
        setSectionState(prev => ({
          ...prev,
          sectionData: tempSection
        }))
        changeJourneySteps("PageGuaranteedIncome4LifeCalculatePremium", "SectionGuaranteedIncome4LifeChooseYourPlan", "GI4LSumAssured", "GI4LSumAssured", "disabled", true)
      }
      // JOURNEY STATE UPDATE
      setJourneyState(prev => ({
        ...prev,
        journeySteps: temp
      }))
  }

  const checkForAnnualIncome = () => {
    if (props?.data?.formControlKey === "AnnualIncome" &&
      sectionState.sectionData?.[props.data.formControlKey]) {
      if (props.journeyIdFromStrapi === "iSelectJourney" || props.journeyIdFromStrapi === "ISelect360Journey") {
        if (sectionState.sectionData?.[props.data.formControlKey] < 250000) {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data.formControlKey]: `This option is not available for this plan`,
            }
          })
          return
        }
      }
      // else if(props.journeyIdFromStrapi === "ISelect360Journey"){
      //   if(sectionState.sectionData["contactCountryCode"] && sectionState.sectionData["contactCountryCode"] !== "+91"){
      //     if(sectionState.sectionData?.[props.data.formControlKey] < 1000000){
      //       setErrorState(prev => {
      //         return {
      //         ...prev,
      //           [props.data.formControlKey]: `This option is not available for this plan`,
      //         }
      //       })
      //       return
      //     }
      //   }else if(sectionState.sectionData["contactCountryCode"] && sectionState.sectionData["contactCountryCode"] === "+91"){
      //     if(sectionState.sectionData?.[props.data.formControlKey] < 250000){
      //       setErrorState(prev => {
      //         return {
      //         ...prev,
      //           [props.data.formControlKey]: `This option is not available for this plan`,
      //         }
      //       })
      //       return
      //     }else{
      //       setErrorState(prev => {
      //         return {
      //         ...prev,
      //           [props.data.formControlKey]: "",
      //         }
      //       })
      //       return
      //     }
      //   }
      // }
    }
    else if (props?.data?.formControlKey === "spouseAnnualIncome" &&
      sectionState.sectionData?.[props.data.formControlKey]) {
      if (sectionState.sectionData["residentStatus"] && sectionState.sectionData["residentStatus"] !== "N") {
        if (sectionState.sectionData?.[props.data.formControlKey] < 1000000) {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data.formControlKey]: `This option is not available for this plan`,
            }
          })
          return
        }
      } else if (sectionState.sectionData["residentStatus"] && sectionState.sectionData["residentStatus"] === "N") {
        if (sectionState.sectionData?.[props.data.formControlKey] < 250000) {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data.formControlKey]: `This option is not available for this plan`,
            }
          })
          return
        } else {
          setErrorState(prev => {
            return {
              ...prev,
              [props.data.formControlKey]: "",
            }
          })
          return
        }
      }
    }
  }

  const invest4GsumAssuredValue = target => {
    let sumAssuredValue
    if(sectionState.sectionData?.["invest4gSumAssuredMultiplier"] && sectionState.sectionData?.["invest4gAmountInvest"] && sectionState.sectionData?.["invest4gPremiumFrequency"]){
       sumAssuredValue = (sectionState.sectionData?.["invest4gAmountInvest"] * sectionState.sectionData?.["invest4gPremiumFrequency"] * sectionState.sectionData?.["invest4gSumAssuredMultiplier"])
    } else if(sectionState.sectionData?.["invest4gAmountInvest"] && sectionState.sectionData?.["invest4gPremiumFrequency"]){
      let frequency 
      if(sectionState.sectionData?.["invest4gPremiumFrequency"] === "12"){
        frequency = 1
      } else if(sectionState.sectionData?.["invest4gPremiumFrequency"] === "06"){
        frequency = 2
      }else if(sectionState.sectionData?.["invest4gPremiumFrequency"] === "01"){
        frequency = 12
      } else {
        frequency = 4
      }
      sumAssuredValue = (sectionState.sectionData?.["invest4gAmountInvest"] * frequency * 10)
    }
    setSectionState(prev => {
      return {
        ...prev,
        sectionData: {
          ...prev.sectionData,
          ["invest4gSumAssured"]: sumAssuredValue,
        },
      }
    })
    setConditionState(prev => {
      return {
        ...prev,
        [`${props.pageId}.${props.sectionId}.invest4gSumAssured`]: sumAssuredValue,
      }
    })
  }

  const aadharMask = (target, name) => {
    if(props.data.formControlKey === "aadharNumber" && props.sectionId === "SectionFlexiEdgeFillProposalPersonalDetail" && !errorState["aadharNumber"]){
      if(name === "mask"){
      if(target.value.length === 12 || target.value.length === 16){
        let aadharNumber = target.value
        let mask = target.value.slice(8)
        let maskAdharValue = `XXXXXXXX${mask}`
        let temp = journeyState?.journeySteps
        let tempPageIndexToChange
        let tempSectionIndexToChange
        let tempComponentGroupIndexToChange
        tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
        tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "SectionFlexiEdgeFillProposalPersonalDetail")
        tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "aadharNumber")
        let tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "aadharNumber")
        temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["regex"] = "/^[a-zA-Z0-9]+$/"
        setSectionState(prev => {
          return ({
            ...prev,
            sectionData: {
              ...prev.sectionData,
              [props.data.formControlKey]: maskAdharValue,
              ["aadharNumberValue"]: aadharNumber
            }
          })
        })
        setJourneyState(prev => {
          return ({
            ...prev,
            journeySteps: temp
          })
        })
        }
      } else {
        let aadharNumberValue
        if(sectionState.sectionData["aadharNumber"] === ""){
           aadharNumberValue = ""
        } else{
            aadharNumberValue = sectionState.sectionData["aadharNumberValue"] || target.value
            let temp = journeyState?.journeySteps
            let tempPageIndexToChange
            let tempSectionIndexToChange
            let tempComponentGroupIndexToChange
            tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
            tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "SectionFlexiEdgeFillProposalPersonalDetail")
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "aadharNumber")
            let tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "aadharNumber")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["regex"] = "/(^[0-9]{12}$)/"
            setJourneyState(prev => {
              return ({
                ...prev,
                journeySteps: temp
              })
            })
            setSectionState(prev => {
              return ({
                ...prev,
                sectionData: {
                  ...prev.sectionData,
                  [props.data.formControlKey]: aadharNumberValue,
                  ["aadharNumberValue"]: aadharNumberValue
                }
              })
            })
          }
      }
    }
    if(props.data.formControlKey === "communicationAadharNumber" && props.sectionId === "SectionFlexiEdgeFillProposalCommunicationAddress" && !errorState["communicationAadharNumber"]){
      if(name === "mask"){
      if(target.value.length === 12 || target.value.length === 16){
        let aadharNumber = target.value
        let mask = target.value.slice(8)
        let maskAdharValue = `XXXXXXXX${mask}`
        let temp = journeyState?.journeySteps
        let tempPageIndexToChange
        let tempSectionIndexToChange
        let tempComponentGroupIndexToChange
        tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
        tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "SectionFlexiEdgeFillProposalCommunicationAddress")
        tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "communicationAadharNumber")
        let tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "communicationAadharNumber")
        temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["regex"] = "/^[a-zA-Z0-9]+$/"
        setSectionState(prev => {
          return ({
            ...prev,
            sectionData: {
              ...prev.sectionData,
              [props.data.formControlKey]: maskAdharValue,
              ["commAadharNumberValue"]: aadharNumber
            }
          })
        })
        setJourneyState(prev => {
          return ({
            ...prev,
            journeySteps: temp
          })
        })
        }
      } else {
        let aadharNumberValue
        if(sectionState.sectionData["communicationAadharNumber"] === ""){
           aadharNumberValue = ""
        } else{
            aadharNumberValue = sectionState.sectionData["commAadharNumberValue"] || target.value
            let temp = journeyState?.journeySteps
            let tempPageIndexToChange
            let tempSectionIndexToChange
            let tempComponentGroupIndexToChange
            tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
            tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "SectionFlexiEdgeFillProposalCommunicationAddress")
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "communicationAadharNumber")
            let tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "communicationAadharNumber")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["regex"] = "/(^[0-9]{12}$)/"
            setJourneyState(prev => {
              return ({
                ...prev,
                journeySteps: temp
              })
            })
            setSectionState(prev => {
              return ({
                ...prev,
                sectionData: {
                  ...prev.sectionData,
                  [props.data.formControlKey]: aadharNumberValue,
                  ["commAadharNumberValue"]: aadharNumberValue
                }
              })
            })
          }
      }
    }
    if(props.data.formControlKey === "aadharNumber" && props.sectionId === "SectionFlexiEdgeFillProposalProposerDetails" && !errorState["aadharNumber"]){
      if(name === "mask"){
      if(target.value.length === 12 || target.value.length === 16){
        let aadharNumber = target.value
        let mask = target.value.slice(8)
        let maskAdharValue = `XXXXXXXX${mask}`
        let temp = journeyState?.journeySteps
        let tempPageIndexToChange
        let tempSectionIndexToChange
        let tempComponentGroupIndexToChange
        tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
        tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "SectionFlexiEdgeFillProposalProposerDetails")
        tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "proposerAadhar")
        let tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "aadharNumber")
        temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["regex"] = "/^[a-zA-Z0-9]+$/"
        setSectionState(prev => {
          return ({
            ...prev,
            sectionData: {
              ...prev.sectionData,
              [props.data.formControlKey]: maskAdharValue,
              ["proposerCurrAadhar"]: aadharNumber
            }
          })
        })
        setJourneyState(prev => {
          return ({
            ...prev,
            journeySteps: temp
          })
        })
        }
      } else {
        let aadharNumberValue
        if(sectionState.sectionData["aadharNumber"] === ""){
           aadharNumberValue = ""
        } else{
            aadharNumberValue = sectionState.sectionData["proposerCurrAadhar"] || target.value
            let temp = journeyState?.journeySteps
            let tempPageIndexToChange
            let tempSectionIndexToChange
            let tempComponentGroupIndexToChange
            tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
            tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "SectionFlexiEdgeFillProposalProposerDetails")
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "proposerAadhar")
            let tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "aadharNumber")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["regex"] = "/(^[0-9]{12}$)/"
            setJourneyState(prev => {
              return ({
                ...prev,
                journeySteps: temp
              })
            })
            setSectionState(prev => {
              return ({
                ...prev,
                sectionData: {
                  ...prev.sectionData,
                  [props.data.formControlKey]: aadharNumberValue,
                  ["proposerCurrAadhar"]: aadharNumberValue
                }
              })
            })
          }
      }
    }
    if(props.data.formControlKey === "communicationAadharNumber" && props.sectionId === "SectionFlexiEdgeFillProposalProposerDetails" && !errorState["communicationAadharNumber"]){
      if(name === "mask"){
      if(target.value.length === 12 || target.value.length === 16){
        let aadharNumber = target.value
        let mask = target.value.slice(8)
        let maskAdharValue = `XXXXXXXX${mask}`
        let temp = journeyState?.journeySteps
        let tempPageIndexToChange
        let tempSectionIndexToChange
        let tempComponentGroupIndexToChange
        tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
        tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "SectionFlexiEdgeFillProposalProposerDetails")
        tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "communicationAadharNumber")
        let tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "communicationAadharNumber")
        temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["regex"] = "/^[a-zA-Z0-9]+$/"
        setSectionState(prev => {
          return ({
            ...prev,
            sectionData: {
              ...prev.sectionData,
              [props.data.formControlKey]: maskAdharValue,
              ["proposerPermAadhar"]: aadharNumber
            }
          })
        })
        setJourneyState(prev => {
          return ({
            ...prev,
            journeySteps: temp
          })
        })
        }
      } else {
        let aadharNumberValue
        if(sectionState.sectionData["communicationAadharNumber"] === ""){
           aadharNumberValue = ""
        } else{
            aadharNumberValue = sectionState.sectionData["proposerPermAadhar"] || target.value
            let temp = journeyState?.journeySteps
            let tempPageIndexToChange
            let tempSectionIndexToChange
            let tempComponentGroupIndexToChange
            tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
            tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "SectionFlexiEdgeFillProposalProposerDetails")
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "communicationAadharNumber")
            let tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "communicationAadharNumber")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["regex"] = "/(^[0-9]{12}$)/"
            setJourneyState(prev => {
              return ({
                ...prev,
                journeySteps: temp
              })
            })
            setSectionState(prev => {
              return ({
                ...prev,
                sectionData: {
                  ...prev.sectionData,
                  [props.data.formControlKey]: aadharNumberValue,
                  ["proposerPermAadhar"]: aadharNumberValue
                }
              })
            })
          }
      }
    }
  }

  const checkForIfscCodes = (target) => {
    if (props.data.formControlKey.toLowerCase().includes("ifsccode") && (target.value && target.value.length === 11)) {
      let payload = {
        "MIR-BNK-IFSC-CD": target.value
      }
      ifscCodeDetails(payload)
        .then(res => {
          console.log(res.data)
          if(res?.data?.allUniqueBankName){
            let bankNameList = res?.data?.allUniqueBankName
            let bankName = []
            bankName.push(bankNameList)
            let temp = journeyState.journeySteps
            let tempPageIndexToChange
            let tempSectionIndexToChange
            let tempComponentGroupIndexToChange
            let tempComponentIndexToChange
            tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
            tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "SectionFlexiEdgeBankDetailsOfProposer")
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "bankName")
            tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankName")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankName
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = false
            if(sectionState.sectionData?.["bankName"] || sectionState.sectionData?.["bankBranchName"]){
              setSectionState(prev => {
                return ({
                  ...prev,
                  sectionData: {
                    ...prev.sectionData,
                    bankName: "",
                    bankBranchName: ""
                  }
                })
              })
            }
            setJourneyState(prev => {
              return ({
                ...prev,
                journeySteps: temp
              })
            })
            if(Object.keys(bankNameList)?.length === 1){
              let bank = Object.keys(bankNameList)[0]
              setSectionState(prev => {
                return ({
                  ...prev,
                  sectionData: {
                    ...prev.sectionData,
                    bankName: bank
                  }
                })
              })
            }
          }
          else{
            let bankName = lookup["bankName"]
            let temp = journeyState.journeySteps
            let tempPageIndexToChange
            let tempSectionIndexToChange
            let tempComponentGroupIndexToChange
            let tempComponentIndexToChange
            tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
            tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "SectionFlexiEdgeBankDetailsOfProposer")
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "bankName")
            tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankName")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankName
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = true
      
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "bankBranchName")
            tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankBranchName")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankName
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = true
            setSectionState(prev => {
              return ({
                ...prev,
                sectionData: {
                  ...prev.sectionData,
                  bankName: "",
                  bankBranchName: ""
                }
              })
            })
            setJourneyState(prev => {
              return ({
                ...prev,
                journeySteps: temp
              })
            })
          }
        })
        .catch((err) => {
          console.log("please enter valid ifsc code", err);
        })
    }

    // for appointee
    if (props.data.formControlKey.toLowerCase().includes("ifsccodeappointee") && (target.value && target.value.length === 11)) {
      let payload = {
        "MIR-BNK-IFSC-CD": target.value
      }
      ifscCodeDetails(payload)
        .then(res => {
          console.log(res.data)
          if(res?.data?.allUniqueBankName){
            let bankNameList = res?.data?.allUniqueBankName
            let bankName = []
            bankName.push(bankNameList)
            let temp = journeyState.journeySteps
            let tempPageIndexToChange
            let tempSectionIndexToChange
            let tempComponentGroupIndexToChange
            let tempComponentIndexToChange
            tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
            tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "FlexiEdgeFillProposalAppointeeSection")
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "nomineeAgeCondition")
            tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankNameAppointee")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankName
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = false
            if(sectionState.sectionData?.["bankNameAppointee"] || sectionState.sectionData?.["bankBranchNameAppointee"]){
              setSectionState(prev => {
                return ({
                  ...prev,
                  sectionData: {
                    ...prev.sectionData,
                    bankNameAppointee: "",
                    bankBranchNameAppointee: ""
                  }
                })
              })
            }
            setJourneyState(prev => {
              return ({
                ...prev,
                journeySteps: temp
              })
            })
            if(Object.keys(bankNameList)?.length === 1){
              let bank = Object.keys(bankNameList)[0]
              setSectionState(prev => {
                return ({
                  ...prev,
                  sectionData: {
                    ...prev.sectionData,
                    bankNameAppointee: bank
                  }
                })
              })
            }
          }
          else{
            let bankName = lookup["bankName"]
            let temp = journeyState.journeySteps
            let tempPageIndexToChange
            let tempSectionIndexToChange
            let tempComponentGroupIndexToChange
            let tempComponentIndexToChange
            tempPageIndexToChange = temp.pages.findIndex(element => element.pageId === "PageFlexiEdgeFillProposalForm")
            tempSectionIndexToChange = temp.pages[tempPageIndexToChange].sections.findIndex(element => element.sectionId === "FlexiEdgeFillProposalAppointeeSection")
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "nomineeAgeCondition")
            tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankNameAppointee")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankName
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = true
      
            tempComponentGroupIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups.findIndex(element => element.componentId === "nomineeAgeCondition")
            tempComponentIndexToChange = temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components.findIndex(element => element.formControlKey === "bankBranchNameAppointee")
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["lookupKey"] = bankName
            temp.pages[tempPageIndexToChange].sections[tempSectionIndexToChange].component_groups[tempComponentGroupIndexToChange].components[tempComponentIndexToChange]["disabled"] = true
            setSectionState(prev => {
              return ({
                ...prev,
                sectionData: {
                  ...prev.sectionData,
                  bankNameAppointee: "",
                  bankBranchNameAppointee: ""
                }
              })
            })
            setJourneyState(prev => {
              return ({
                ...prev,
                journeySteps: temp
              })
            })
          }
        })
        .catch((err) => {
          console.log("please enter valid ifsc code", err);
        })
    }

  }
  console.log(props, "lo")

   useEffect(() => {
      if (props.sectionId === "FDsuccessfullPaymentDetails" && props.data.formControlKey === "countryGiftCity" && conditionState["PageFutureDollarGiftCityCalculateFundValue.FutureDollarGiftCityPersonelDetailsSection.GIFTcountry"]){
        setSectionState(prev => {
          return {
            ...prev,
            sectionData : {
              ...prev.sectionData,
              "countryGiftCity" : conditionState["PageFutureDollarGiftCityCalculateFundValue.FutureDollarGiftCityPersonelDetailsSection.GIFTcountry"] === "IN" ? "INDIA" : lookup.country[0][conditionState["PageFutureDollarGiftCityCalculateFundValue.FutureDollarGiftCityPersonelDetailsSection.GIFTcountry"]]
            }
          }
        })
      }
    }, [])

  return (
    <>
      <InputContainer
        channel={props.channel}
        className={`form-group input-container ${props.data.disabled ? "disable-input" : ""
          }
        ${props.data.html ? "input-auto-height" : ""
          }`}
      >
        <label
          className={`input-label ${props.data.label ? "" : "no-label"} `}
          htmlFor={props.data.id ? props.data.id : props.data.label}
        >
          {(getValueFromJourney(
            journeyState.lookup.label,
            props.data.label
          )) || props.data.label}
          {props.data.tooltip &&
            <span style={{ marginLeft: "10px" }} className="tooltip-col text-tool-tip-design existing-tool-tip">
              <div className="tool">i</div>
              <span className="tooltiptext4 tooltiptext5 tooltiptext4-align-list " style={{ width: "22rem", height: "fit-content", top: "auto", pointerEvents: "none" }}>
                {Parser((getValueFromJourney(
                  journeyState.lookup.tooltip,
                  props.data.tooltip
                )))}
              </span>
            </span>
          }
        </label>
        <div className="form-control str-input">
          <input
            type="text"
            style={{
              width:
                numToWordFormControlKey.includes(props.data.formControlKey) ||
                  props.data.formControlKey.toLowerCase().includes("sumassured")
                  ? "60%"
                  : "100%",
            }}
            autoComplete="new-password"
            name={+Date.now()}
            required={props.data.required}
            placeholder={(getValueFromJourney(
              journeyState.lookup.placeholder,
              props.data.placeholder
            )) || props.data.placeholder}
            disabled={props.data.disabled}
            id={props.data.id ? props.data.id : props.data.label}
            onChange={handleChange}
            onClick={e => {
              aadharMask(e.target, "nonMask")
              props.data.isMasked && dataMask(e.target,"nonMask")
            }}
            value={
              props.data.isMasked ? sectionState.sectionData?.[props.data.maskedKey]
              : sectionState.sectionData?.[props.data.formControlKey]
                ? numToWordFormControlKey.includes(props.data.formControlKey)
                  ? applyComma(
                      sectionState.sectionData[props.data.formControlKey]
                    )
                  : sectionState.sectionData[props.data.formControlKey]
                : ""
            }
            onBlur={e => {
              checkForIfscCodes(e.target)
              validate(e.target)
              aadharMask(e.target, "mask")
              checkForAnnualIncome()
              invest4GsumAssuredValue(e.target)
              props.data.isMasked && dataMask(e.target,"mask")
              if(journeyState.journeyIdFromStrapi === "guaranteedIncome4LifeJourney") {
                setSumAssuredValueForGI4L()
              }
              let targetValue = e.target.value
              if (
                numToWordFormControlKey.includes(props.data.formControlKey) ||
                props.data.formControlKey.toLowerCase().includes("sumassured")
              ) {
                let regex = new RegExp(/^\d+$/)
                if (regex.test(targetValue.replace(/,/g, ""))) {
                  let temp = targetValue.split(",")
                  targetValue = temp.join("")
                } else if (targetValue == "") {
                  targetValue = ""
                }
              }
              props.saveApplicationAndCallBi &&
                props.saveApplicationAndCallBi(
                  props.data.formControlKey,
                  targetValue
                )
            }}
          />
          <span
            style={{
              fontSize: "8px",
            }}
          >
            {numToWordFormControlKey.includes(props.data.formControlKey) ||
              props.data.formControlKey.toLowerCase().includes("sumassured")
              ? showIns ? insWords(sectionState.sectionData?.[props.data.formControlKey],false,"international")
              : null : null}
          </span>
        </div>
        <span className="input-error2">
          {(props.showError || showLocalError) &&
            errorState[props.data.formControlKey]}
        </span>
        <div dangerouslySetInnerHTML={{ __html: props.data.html }} ></div>
      </InputContainer>
    </>
  )
}
